import React, { useEffect, useState, useRef } from "react";
import { Download, Check, Loader2, Trash2, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { BIBLE_BOOKS } from "../data/bibleBooks";
import { fetchChapter } from "../lib/bible";

const STORAGE_FLAG = "cm_bible_offline_v1"; // {downloaded:boolean, at:ISO, total:number}

const totalChapters = BIBLE_BOOKS.reduce((sum, b) => sum + b.chapters, 0);

const readFlag = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_FLAG);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeFlag = (data) => {
  try {
    window.localStorage.setItem(STORAGE_FLAG, JSON.stringify(data));
  } catch { /* ignore */ }
};

/**
 * Card visible on /biblia letting the user pre-cache all 66 books for offline
 * reading. Streams chapters with throttling so we don't hammer bible-api.com.
 *
 * Strategy:
 *  - 6 concurrent fetches max (pool).
 *  - Updates a progress bar after each chapter.
 *  - Service Worker (sw.js) handles HTTP cache (BIBLE_CACHE, cache-first).
 *  - fetchChapter() also writes localStorage cm_bible_cache_v1, so reading
 *    works even when the SW hasn't activated yet (e.g. dev preview).
 *  - Idempotent: re-running just refreshes any failed chapters.
 */
export default function OfflineBibleDownloader() {
  const [flag, setFlag] = useState(readFlag);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: totalChapters, currentBook: "" });
  const cancelRef = useRef(false);

  // Reset cancel flag on new run.
  useEffect(() => { cancelRef.current = false; }, [downloading]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setProgress({ done: 0, total: totalChapters, currentBook: "" });

    // Build the full list of (slug, chapter) tuples in canonical order.
    const tuples = [];
    for (const b of BIBLE_BOOKS) {
      for (let c = 1; c <= b.chapters; c++) {
        tuples.push({ slug: b.slug, name: b.name, chapter: c });
      }
    }

    // Throttle: 6 concurrent requests.
    const POOL = 6;
    let cursor = 0;
    let done = 0;
    let failed = 0;

    const next = async () => {
      while (true) {
        if (cancelRef.current) return;
        const i = cursor++;
        if (i >= tuples.length) return;
        const { slug, name, chapter } = tuples[i];
        try {
          await fetchChapter(slug, chapter);
        } catch {
          failed++;
        }
        done++;
        setProgress({ done, total: tuples.length, currentBook: name });
      }
    };

    await Promise.all(Array.from({ length: POOL }, () => next()));

    if (cancelRef.current) {
      toast.info("Download cancelado.");
    } else if (failed === 0) {
      const data = { downloaded: true, at: new Date().toISOString(), total: tuples.length };
      writeFlag(data);
      setFlag(data);
      toast.success("Bíblia disponível offline!");
    } else {
      toast.warning(`Download concluído com ${failed} capítulos pendentes, tente novamente quando estiver com sinal melhor.`);
    }
    setDownloading(false);
  };

  const handleCancel = () => { cancelRef.current = true; };

  const handleClear = () => {
    try {
      window.localStorage.removeItem("cm_bible_cache_v1");
      window.localStorage.removeItem(STORAGE_FLAG);
    } catch { /* ignore */ }
    if (navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "BIBLE_CACHE_CLEAR" });
    }
    setFlag(null);
    toast.success("Cache offline limpo.");
  };

  const pct = Math.round((progress.done / progress.total) * 100);

  // Already downloaded, show success state with option to clear.
  if (flag?.downloaded && !downloading) {
    return (
      <section
        data-testid="offline-bible-card"
        data-state="ready"
        className="bg-white border border-border rounded-2xl p-5"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0">
            <Check className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Modo offline</p>
            <p className="text-sm text-navy/80 mt-1.5">
              Bíblia disponível offline
            </p>
            <p className="text-[11px] text-navy/55 mt-1">
              {flag.total} capítulos prontos. Você pode ler sem internet.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClear}
            data-testid="offline-bible-clear"
            aria-label="Limpar cache offline"
            className="text-navy/45 hover:text-rose-500 p-2 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="offline-bible-card"
      data-state={downloading ? "downloading" : "idle"}
      className="bg-white border border-border rounded-2xl p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Modo offline</p>
          <p className="text-sm text-navy/80 mt-1.5">
            {downloading ? "Baixando a Bíblia inteira…" : "Baixar Bíblia para ler offline"}
          </p>
          <p className="text-[11px] text-navy/55 mt-1">
            {downloading
              ? `${progress.done} / ${progress.total} capítulos${progress.currentBook ? ` · ${progress.currentBook}` : ""}`
              : `Os 66 livros (${totalChapters} capítulos, ~3 MB). Funciona em qualquer lugar.`}
          </p>

          {downloading && (
            <div className="mt-3 h-1.5 bg-[hsl(var(--parchment))] rounded-full overflow-hidden" data-testid="offline-bible-progress">
              <div
                className="h-full bg-gold transition-all duration-200"
                style={{ width: `${pct}%` }}
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                role="progressbar"
              />
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            {downloading ? (
              <button
                type="button"
                onClick={handleCancel}
                data-testid="offline-bible-cancel"
                className="text-[11px] uppercase tracking-widest text-navy/65 hover:text-rose-500 px-1 py-1 transition-colors"
              >
                Cancelar
              </button>
            ) : (
              <button
                type="button"
                onClick={handleDownload}
                data-testid="offline-bible-download"
                className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Baixar agora
              </button>
            )}
            {!navigator.onLine && !downloading && (
              <span className="inline-flex items-center gap-1 text-[11px] text-rose-500">
                <WifiOff className="w-3 h-3" /> Conecte-se primeiro
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
